// @ts-nocheck
import React, { useState, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { Button } from '../ui/button';
import { Download, Printer, Users } from 'lucide-react';
import { generateTeamQRCode } from '../../utils/qrGenerator';
import Papa from 'papaparse';
import { Template } from '@/types/packet-management';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface PacketGeneratorProps {
  eventId: string;
  eventName: string;
  teamId?: string;
  teamName?: string;
  templateData?: Template;
}

interface AthleteData {
  id: string;
  profile_id: string;
  first_name: string;
  last_name: string;
  position: string;
  jersey_number: string;
  grad_year: string;
  email: string;
  cell_phone: string;
  profiles?: {
    status: string;
    user_id: string | null;
    username: string;
  };
  coach_teams?: {
    id: string;
    name: string;
    head_coach_email: string;
    head_coach_name: string;
    head_coach_cell: string;
    age_group: string;
  };
}

interface DebugStep {
  step: string;
  status: 'pending' | 'completed' | 'error';
  count?: number;
  data?: any;
  error?: string;
  duration?: number;
}

const PacketGenerator: React.FC<PacketGeneratorProps> = ({ 
  eventId, 
  eventName,
  teamId,
  teamName,
  templateData
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [debugSteps, setDebugSteps] = useState<DebugStep[]>([]);
  const [showDebug, setShowDebug] = useState(true);
  const printRef = useRef<HTMLDivElement>(null);

  const updateDebugStep = (stepName: string, status: 'pending' | 'completed' | 'error', data?: any) => {
    setDebugSteps(prev => {
      const existingIndex = prev.findIndex(step => step.step === stepName);
      const newStep: DebugStep = {
        step: stepName,
        status,
        count: Array.isArray(data) ? data.length : undefined,
        data: Array.isArray(data) ? data.slice(0, 2) : data,
        error: status === 'error' ? data?.message || String(data) : undefined,
        duration: status === 'completed' ? Date.now() : undefined
      };
      
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = newStep;
        return updated;
      }
      return [...prev, newStep];
    });
  };

  const fetchRosterData = async (): Promise<AthleteData[]> => {
    const startTime = performance.now();
    setDebugSteps([]);
    console.log('🔍 ===== DEBUG: Starting fetchRosterData (Direct Query) =====');
    console.log('🔍 DEBUG: EventId received:', eventId, 'Type:', typeof eventId);
    console.log('🔍 DEBUG: TeamId received:', teamId, 'Type:', typeof teamId);
    console.log('🔍 DEBUG: Start time:', new Date().toISOString());
    
    try {
      if (!teamId) {
        console.log('🔍 DEBUG: ===== NO TEAM ID PROVIDED =====');
        updateDebugStep('Team ID Check', 'error', 'No team ID provided');
        return [];
      }

      // Step 1: Get team rosters directly using teamId
      updateDebugStep('Step 1: Get team rosters by teamId', 'pending');
      const step1StartTime = performance.now();
      console.log('🔍 DEBUG: ===== STEP 1: Getting team rosters by teamId =====');
      console.log('🔍 DEBUG: Querying coach_team_rosters with teamId:', teamId);
      
      const { data: rosters, error: rostersError } = await supabase
        .from('coach_team_rosters')
        .select(`
          *,
          coach_teams!inner (
            id,
            name,
            head_coach_email,
            head_coach_name,
            head_coach_cell,
            sport_id,
            age_group,
            gender
          ),
          profiles:profile_id (
            status,
            user_id,
            username,
            name,
            role
          )
        `)
        .eq('team_id', teamId);

      const step1Duration = performance.now() - step1StartTime;
      console.log('🔍 DEBUG: Step 1 completed in:', step1Duration.toFixed(2), 'ms');
      console.log('🔍 DEBUG: Team rosters found - Count:', rosters?.length || 0);
      console.log('🔍 DEBUG: Team rosters - Full data:', JSON.stringify(rosters, null, 2));
      console.log('🔍 DEBUG: Rosters error:', rostersError);

      if (rostersError) {
        console.error('❌ DEBUG: ERROR in Step 1 - team rosters:', rostersError);
        console.error('❌ DEBUG: Error details:', JSON.stringify(rostersError, null, 2));
        updateDebugStep('Step 1: Get team rosters by teamId', 'error', rostersError);
        throw rostersError;
      }

      updateDebugStep('Step 1: Get team rosters by teamId', 'completed', rosters);

      if (!rosters || rosters.length === 0) {
        console.log('🔍 DEBUG: ===== NO TEAM ROSTERS FOUND =====');
        console.log('🔍 DEBUG: No rosters found for teamId:', teamId);
        return [];
      }

      const finalData = rosters || [];
      const totalDuration = performance.now() - startTime;
      
      console.log('🔍 DEBUG: ===== FINAL RESULTS =====');
      console.log('🔍 DEBUG: Total execution time:', totalDuration.toFixed(2), 'ms');
      console.log('🔍 DEBUG: Final roster data count:', finalData.length);
      console.log('🔍 DEBUG: Final roster data structure:', JSON.stringify(finalData, null, 2));
      console.log('🔍 DEBUG: Athletes with profiles:', finalData.filter(r => r.profiles).length);
      console.log('🔍 DEBUG: Athletes with usernames:', finalData.filter(r => r.profiles?.username).length);
      console.log('🔍 DEBUG: Team data available:', finalData.map(r => ({
        team_name: r.coach_teams?.name,
        coach_email: r.coach_teams?.head_coach_email,
        athlete_name: `${r.first_name} ${r.last_name}`
      })));
      console.log('🔍 DEBUG: End time:', new Date().toISOString());
      console.log('🔍 ===== DEBUG: fetchRosterData COMPLETE =====');
      
      return finalData;
    } catch (error) {
      const totalDuration = performance.now() - startTime;
      console.error('❌ DEBUG: ===== CRITICAL ERROR in fetchRosterData =====');
      console.error('❌ DEBUG: Error occurred after:', totalDuration.toFixed(2), 'ms');
      console.error('❌ DEBUG: Error type:', typeof error);
      console.error('❌ DEBUG: Error message:', error?.message || 'No message');
      console.error('❌ DEBUG: Full error object:', JSON.stringify(error, null, 2));
      console.error('❌ DEBUG: Error stack:', error?.stack);
      console.error('❌ DEBUG: ===== END ERROR DETAILS =====');
      updateDebugStep('Critical Error', 'error', error);
      throw error;
    }
  };

  // Generate field display names dynamically
  const getFieldDisplayName = (fieldName: string): string => {
    // Convert snake_case or camelCase to Title Case
    return fieldName
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();
  };

  // Get dynamic columns from template, always include Profile at the end
  const getDynamicColumns = () => {
    const templateFields = templateData?.selectedFields || [];
    return [...templateFields, 'profile']; // Always include profile column
  };

  // Get field value from athlete data dynamically
  const getFieldValue = (athlete: AthleteData, fieldName: string): string => {
    // Handle profile column specially
    if (fieldName === 'profile') {
      return 'profile';
    }

    // Try to get value from athlete object first
    if (athlete.hasOwnProperty(fieldName)) {
      const value = (athlete as any)[fieldName];
      return value || 'N/A';
    }

    // Try to get value from coach_teams nested object
    if (athlete.coach_teams && athlete.coach_teams.hasOwnProperty(fieldName)) {
      const value = (athlete.coach_teams as any)[fieldName];
      return value || 'N/A';
    }

    // Handle special mappings for nested data
    switch (fieldName) {
      case 'team_name':
      case 'name':
        return athlete.coach_teams?.name || 'N/A';
      case 'head_coach_name':
        return athlete.coach_teams?.head_coach_name || 'N/A';
      case 'head_coach_email':
        return athlete.coach_teams?.head_coach_email || 'N/A';
      case 'head_coach_cell':
        return athlete.coach_teams?.head_coach_cell || 'N/A';
      case 'age_group':
        return athlete.coach_teams?.age_group || 'N/A';
      default:
        return 'N/A';
    }
  };

  const generatePrintableHTML = async (athletes: AthleteData[]): Promise<string> => {
    console.log('🔧 DEBUG: PacketGenerator - Starting QR generation for athletes:', athletes.length);
    
    const qrCodePromises = athletes.map(async (athlete, index) => {
      try {
        // Only generate QR codes for confirmed athletes with username
        if (athlete.profiles?.username) {
          console.log(`✅ DEBUG: Generating QR for athlete ${index + 1}:`, {
            name: `${athlete.first_name} ${athlete.last_name}`,
            username: athlete.profiles.username,
            profile_id: athlete.profile_id
          });
          const qrCode = await generateTeamQRCode(athlete.profile_id);
          return { ...athlete, qrCode };
        } else {
          console.log(`❌ DEBUG: No QR for athlete ${index + 1}:`, {
            name: `${athlete.first_name} ${athlete.last_name}`,
            hasProfiles: !!athlete.profiles,
            username: athlete.profiles?.username || 'NO USERNAME',
            user_id: athlete.profiles?.user_id || 'NO USER_ID'
          });
          return { ...athlete, qrCode: null };
        }
      } catch (error) {
        console.error(`❌ Error generating QR code for athlete ${athlete.id}:`, error);
        return { ...athlete, qrCode: null };
      }
    });

    const athletesWithQR = await Promise.all(qrCodePromises);
    
    const confirmedCount = athletesWithQR.filter(a => a.qrCode !== null).length;
    console.log(`🔧 DEBUG: QR Generation complete - ${confirmedCount}/${athletesWithQR.length} athletes have QR codes`);
    
    // Get template branding elements
    const headerText = templateData?.headerText || eventName;
    const footerText = templateData?.footerText || 'RecruitLook Team Roster';
    const logoUrl = templateData?.logoUrl;
    
    return `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>${headerText} - Team Roster</title>
    <style>
        body { 
            font-family: Arial, sans-serif; 
            margin: 20px;
            background: white;
        }
        .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #333;
            padding-bottom: 20px;
        }
        .template-logo {
            max-width: 150px;
            max-height: 80px;
            margin-bottom: 15px;
        }
        .header-title {
            font-size: 28px;
            font-weight: bold;
            margin-bottom: 10px;
            color: #333;
        }
        .team-info {
            font-size: 18px;
            color: #666;
            margin-bottom: 10px;
        }
        .roster-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
        }
        .roster-table th,
        .roster-table td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
        }
        .roster-table th {
            background-color: #f2f2f2;
            font-weight: bold;
        }
        .footer {
            margin-top: 40px;
            text-align: center;
            color: #666;
            font-size: 12px;
            border-top: 1px solid #ddd;
            padding-top: 20px;
        }
        @media print {
            body { margin: 0; }
        }
    </style>
</head>
<body>
    <div class="header">
        ${logoUrl ? `<img src="${logoUrl}" alt="Logo" class="template-logo" />` : ''}
        <div class="header-title">${headerText}</div>
        ${teamName ? `<div class="team-info">${teamName}</div>` : ''}
        <div style="color: #666; font-size: 14px; margin-top: 10px;">
            Generated: ${new Date().toLocaleDateString()} | 
            Total Athletes: ${athletesWithQR.length} | 
            With QR Codes: ${confirmedCount}
        </div>
    </div>
    
    <table class="roster-table">
        <thead>
            <tr>
                ${getDynamicColumns().map(column => {
                  if (column === 'profile') {
                    return '<th>Profile</th>';
                  }
                  return `<th>${getFieldDisplayName(column)}</th>`;
                }).join('')}
            </tr>
        </thead>
        <tbody>
            ${athletesWithQR.map(athlete => `
                <tr>
                    ${getDynamicColumns().map(column => {
                      if (column === 'profile') {
                        return `<td style="text-align: center;">
                            ${athlete.qrCode ? `
                                <img src="${athlete.qrCode}" alt="QR Code" style="width: 50px; height: 50px;" />
                            ` : `
                                No Profile
                            `}
                        </td>`;
                      }
                      return `<td>${getFieldValue(athlete, column)}</td>`;
                    }).join('')}
                </tr>
            `).join('')}
        </tbody>
    </table>
    
    <div class="footer">
        <p>${footerText}</p>
        <p style="font-size: 10px; margin-top: 10px;">DEBUG: Only athletes with confirmed profiles get QR codes</p>
    </div>
</body>
</html>`;
  };

  const generateCSVData = (athletes: AthleteData[]): string => {
    const csvData = athletes.map(athlete => {
      const row: { [key: string]: string } = {};
      
      // Add dynamic columns based on template
      getDynamicColumns().forEach(column => {
        if (column === 'profile') {
          row['Username'] = athlete.profiles?.username || '';
          row['Profile ID'] = athlete.profile_id;
        } else {
          const displayName = getFieldDisplayName(column);
          row[displayName] = getFieldValue(athlete, column);
        }
      });
      
      return row;
    });

    return Papa.unparse(csvData);
  };

  const handlePrintGeneration = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      const athletes = await fetchRosterData();
      
      if (athletes.length === 0) {
        setError('No athletes found for this event.');
        return;
      }

      const htmlContent = await generatePrintableHTML(athletes);
      
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();
        
        // Trigger print dialog after content loads
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 250);
        };
      } else {
        setError('Unable to open print window. Please check your popup blocker settings.');
      }
    } catch (error) {
      console.error('Error generating printable roster:', error);
      setError('Failed to generate printable roster. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCSVDownload = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      const athletes = await fetchRosterData();
      
      if (athletes.length === 0) {
        setError('No athletes found for this event.');
        return;
      }

      const csvContent = generateCSVData(athletes);
      
      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `${eventName.replace(/[^a-z0-9]/gi, '_')}_confirmed_roster.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating CSV:', error);
      setError('Failed to generate CSV file. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !eventId) return;

    if (file.type !== 'application/pdf') {
      setError('Please upload a PDF file only.');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      // Upload PDF to Supabase storage
      const filename = `${eventId}_purchasable_packet_${Date.now()}.pdf`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('purchasable-packets')
        .upload(`events/${eventId}/${filename}`, file, {
          contentType: 'application/pdf',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('purchasable-packets')
        .getPublicUrl(`events/${eventId}/${filename}`);

      // Update event with purchasable packet URL
      const { error: updateError } = await supabase
        .from('events')
        .update({ purchasable_packet_url: publicUrl })
        .eq('id', eventId);

      if (updateError) throw updateError;

      setError('✅ PDF packet uploaded and set as purchasable for this event!');
    } catch (error) {
      console.error('Error uploading PDF packet:', error);
      setError('Failed to upload PDF packet. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* DEBUG: Changes made - Update v2.1 */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <p className="text-blue-800 text-sm font-medium">🔧 DEBUG: PacketGenerator v2.1 - QR filtering active</p>
      </div>
      
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <Users className="h-4 w-4" />
        <span>Generate roster packets for all registered athletes</span>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Debug Panel */}
      {showDebug && debugSteps.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-800">Debug Information</h3>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowDebug(!showDebug)}
              className="text-xs"
            >
              {showDebug ? 'Hide' : 'Show'} Debug
            </Button>
          </div>
          
          <div className="space-y-2">
            {debugSteps.map((step, index) => (
              <div key={index} className="border border-gray-200 rounded p-3 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{step.step}</span>
                  <div className="flex items-center gap-2">
                    {step.count !== undefined && (
                      <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                        Count: {step.count}
                      </span>
                    )}
                    <span className={`text-xs px-2 py-1 rounded ${
                      step.status === 'completed' ? 'bg-green-100 text-green-800' :
                      step.status === 'error' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {step.status}
                    </span>
                  </div>
                </div>
                
                {step.error && (
                  <div className="text-xs text-red-600 bg-red-50 p-2 rounded mb-2">
                    Error: {step.error}
                  </div>
                )}
                
                {step.data && Array.isArray(step.data) && step.data.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-xs text-gray-600 cursor-pointer hover:text-gray-800">
                      View Sample Data ({step.data.length} items shown)
                    </summary>
                    <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-auto max-h-32">
                      {JSON.stringify(step.data, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Purchasable Packet */}
      <div className="border-t pt-4">
        <h4 className="font-medium mb-3">Upload Purchasable Packet</h4>
        <div className="flex items-center gap-3">
          <input
            type="file"
            accept=".pdf"
            onChange={handleFileUpload}
            disabled={isGenerating}
            className="text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
          />
          <span className="text-sm text-gray-600">Upload a PDF packet for customers to purchase</span>
        </div>
      </div>

      {/* Individual Team Generation */}
      <div className="border-t pt-4">
        <h4 className="font-medium mb-3">Individual Team Generation</h4>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handlePrintGeneration}
            disabled={isGenerating}
            className="flex items-center gap-2"
          >
            <Printer className="h-4 w-4" />
            {isGenerating ? 'Generating...' : 'Print Roster'}
          </Button>

          <Button
            onClick={handleCSVDownload}
            disabled={isGenerating}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            {isGenerating ? 'Generating...' : 'Download CSV'}
          </Button>
        </div>
      </div>

    </div>
  );
};

export { PacketGenerator };